import type { ReactElement } from 'react';
import {
  Toolbar,
  ToolbarButton,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@coreai/fluentui-react';

export interface TagSwitchOption<Value extends string = string> {
  value: Value;
  label: string;
  icon?: ReactElement;
  disabled?: boolean;
}

export interface TagSwitchProps<Value extends string = string> {
  options: readonly TagSwitchOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  'aria-label': string;
  'data-testid'?: string;
  className?: string;
  /** Hide text on options with icons; their labels remain available as tooltips. */
  iconOnly?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    flexShrink: 0,
    width: 'fit-content',
    boxSizing: 'border-box',
    gap: tokens.spacingHorizontalXXS,
    padding: tokens.spacingVerticalXXS,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  button: {
    minWidth: '32px',
    minHeight: '32px',
  },
});

/**
 * Controlled, single-selection button group with Fluent toolbar keyboard navigation.
 * Options render text, an icon with text, or (with iconOnly) a labeled icon button.
 */
export function TagSwitch<Value extends string>({
  options,
  value,
  onChange,
  iconOnly = false,
  className,
  ...props
}: TagSwitchProps<Value>) {
  const styles = useStyles();

  return (
    <Toolbar {...props} className={mergeClasses(styles.root, className)} size="medium">
      {options.map((option) => {
        const hideLabel = iconOnly && !!option.icon;
        const button = (
          <ToolbarButton
            key={option.value}
            className={styles.button}
            aria-label={option.label}
            aria-pressed={value === option.value}
            appearance={value === option.value ? 'primary' : 'subtle'}
            disabled={option.disabled ?? false}
            icon={option.icon ?? null}
            onClick={() => {
              if (option.value !== value) onChange(option.value);
            }}
          >
            {hideLabel ? undefined : option.label}
          </ToolbarButton>
        );

        return hideLabel ? (
          <Tooltip key={option.value} content={option.label} relationship="description" withArrow>
            {button}
          </Tooltip>
        ) : button;
      })}
    </Toolbar>
  );
}
